import * as THREE from 'three';

/**
 * Close isometric camera orbiting a target on the table (SPEC §15): drag/two fingers to rotate,
 * wheel/pinch to zoom. Runs at full frame rate with critically damped smoothing.
 */
export class TableCamera {
  readonly camera: THREE.PerspectiveCamera;
  target = new THREE.Vector3(0, 0, 0);
  private goalTarget = new THREE.Vector3(0, 0, 0);
  yaw = Math.PI / 4;
  private goalYaw = Math.PI / 4;
  pitch = 0.95;
  private goalPitch = 0.95;
  dist = 22;
  private goalDist = 22;
  minDist = 7;
  maxDist = 34;
  /** Pan bounds (world radius). */
  bound = 18;
  private shake = 0;

  constructor(aspect: number) {
    // The camera never comes closer than ~3.5 units to anything (minDist 7, lowest pitch 0.55), so the near
    // plane sits at 1 instead of 0.5: twice the depth precision, which phones (16/24-bit depth) need to keep
    // flat marks on the ground from flickering.
    this.camera = new THREE.PerspectiveCamera(32, aspect, 1, 200);
    this.apply();
  }

  rotate(dYaw: number, dPitch = 0): void {
    this.goalYaw += dYaw;
    this.goalPitch = THREE.MathUtils.clamp(this.goalPitch + dPitch, 0.55, 1.25);
  }

  zoom(factor: number): void {
    this.goalDist = THREE.MathUtils.clamp(this.goalDist * factor, this.minDist, this.maxDist);
  }

  /** Pan in screen-aligned ground directions. */
  pan(dx: number, dz: number): void {
    const s = this.goalDist / 22;
    const fwd = new THREE.Vector3(-Math.sin(this.goalYaw), 0, -Math.cos(this.goalYaw));
    const right = new THREE.Vector3(Math.cos(this.goalYaw), 0, -Math.sin(this.goalYaw));
    this.goalTarget.addScaledVector(right, dx * s).addScaledVector(fwd, dz * s);
    if (this.goalTarget.length() > this.bound) this.goalTarget.setLength(this.bound);
  }

  focus(p: THREE.Vector3, immediate = false): void {
    this.goalTarget.set(p.x, 0, p.z);
    if (this.goalTarget.length() > this.bound) this.goalTarget.setLength(this.bound);
    if (immediate) this.target.copy(this.goalTarget);
  }

  setYaw(y: number, immediate = false): void {
    this.goalYaw = y;
    if (immediate) this.yaw = y;
  }

  setDistance(d: number, immediate = false): void {
    this.goalDist = THREE.MathUtils.clamp(d, this.minDist, this.maxDist);
    if (immediate) this.dist = this.goalDist;
  }

  addShake(amount: number): void {
    this.shake = Math.min(0.6, this.shake + amount);
  }

  update(dt: number): void {
    const k = 1 - Math.exp(-dt * 9);
    this.yaw += (this.goalYaw - this.yaw) * k;
    this.pitch += (this.goalPitch - this.pitch) * k;
    this.dist += (this.goalDist - this.dist) * k;
    this.target.lerp(this.goalTarget, 1 - Math.exp(-dt * 6));
    this.shake *= Math.exp(-dt * 5);
    this.apply();
  }

  private apply(): void {
    const c = this.camera;
    const h = Math.sin(this.pitch) * this.dist;
    const flat = Math.cos(this.pitch) * this.dist;
    c.position.set(this.target.x + Math.sin(this.yaw) * flat, this.target.y + h, this.target.z + Math.cos(this.yaw) * flat);
    if (this.shake > 0.01) {
      c.position.x += (Math.random() - 0.5) * this.shake;
      c.position.y += (Math.random() - 0.5) * this.shake * 0.5;
    }
    c.lookAt(this.target.x, this.target.y + 0.4, this.target.z);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    // Portrait phones: widen the view so the bastion fits.
    this.camera.fov = aspect < 0.8 ? 46 : aspect < 1.2 ? 38 : 32;
    this.camera.updateProjectionMatrix();
  }
}
